/**
 * OpenClose Style03 - Smart Blind mobile position slider
 */
import { useState, useEffect, useMemo, useRef } from 'react'
import type { TraitControlProps } from './types'
import { resolveRangeFromAttributes } from './traitStyleConfig'
import './OpenCloseStyle03.css'

export function OpenCloseStyle03({
  state,
  attributes,
  onCommand,
  disabled = false,
}: TraitControlProps) {
  const { min: MIN, max: MAX } = useMemo(
    () => resolveRangeFromAttributes(attributes, 'OpenClose', 'openClose'),
    [attributes]
  )
  const raw = (state?.openPercent ?? state?.openClosePercent ?? state?.position) as number | undefined
  const current = typeof raw === 'number' ? Math.max(MIN, Math.min(MAX, Math.round(raw))) : Math.round((MIN + MAX) / 2)
  const [percent, setPercent] = useState(current)
  const lastSentRef = useRef<{ value: number; at: number } | null>(null)
  const closedPercent = Math.max(0, Math.min(100, Math.round(percent)))
  const stateToken = percent > MIN ? 'on open' : 'off close'

  useEffect(() => {
    const v = typeof raw === 'number' ? Math.max(MIN, Math.min(MAX, Math.round(raw))) : Math.round((MIN + MAX) / 2)
    setPercent(v)
  }, [raw, MIN, MAX])

  const handlePreview = (nextValue: number) => {
    const safe = Math.max(MIN, Math.min(MAX, Math.round(nextValue)))
    setPercent(safe)
  }

  const handleCommit = (nextValue: number) => {
    const safe = Math.max(MIN, Math.min(MAX, Math.round(nextValue)))
    setPercent(safe)
    const now = Date.now()
    if (
      lastSentRef.current &&
      lastSentRef.current.value === safe &&
      now - lastSentRef.current.at < 250
    ) return
    lastSentRef.current = { value: safe, at: now }
    // Turing ExecuteCommand expects OpenClose + openPercent.
    void onCommand('openClose', { openPercent: safe })
  }

  return (
    <div className="trait-openclose-style03">
      <div
        className="trait-openclose-style03__visual"
        data-css-slot="openCloseVisual"
        data-css-slot-label="OpenClose Visual"
        data-css-state={stateToken}
      >
        <div className="trait-openclose-style03__visual-window">
          <div
            className="trait-openclose-style03__blind"
            style={{ '--blind-closed': `${closedPercent}%` } as React.CSSProperties}
          >
            <div className="trait-openclose-style03__slat trait-openclose-style03__slat--1" />
            <div className="trait-openclose-style03__slat trait-openclose-style03__slat--2" />
            <div className="trait-openclose-style03__slat trait-openclose-style03__slat--3" />
            <div className="trait-openclose-style03__slat trait-openclose-style03__slat--4" />
            <div className="trait-openclose-style03__handle" />
          </div>
        </div>
        <div
          className="trait-openclose-style03__badge"
          data-css-slot="openCloseBadge"
          data-css-slot-label="OpenClose Badge"
          data-css-state={stateToken}
        >
          <span className="trait-openclose-style03__badge-value">{closedPercent}</span>
          <span className="trait-openclose-style03__badge-unit">% Closed</span>
        </div>
      </div>
      <div className="trait-openclose-style03__header">
        <span
          className="trait-openclose-style03__title"
          data-css-slot="openCloseTitle"
          data-css-slot-label="OpenClose Title"
          data-css-state={stateToken}
        >
          Position
        </span>
        <span
          className="trait-openclose-style03__value"
          data-css-slot="openCloseValue"
          data-css-slot-label="OpenClose Value"
          data-css-state={stateToken}
        >
          {closedPercent}%
        </span>
      </div>
      <div
        className="trait-openclose-style03__control"
        data-css-slot="openCloseControl"
        data-css-slot-label="OpenClose Control"
        data-css-state={stateToken}
      >
        <div className="trait-openclose-style03__track" />
        <div
          className="trait-openclose-style03__fill"
          style={{ '--fill-percent': `${((percent - MIN) / (MAX - MIN || 1)) * 100}%` } as React.CSSProperties}
        />
        <input
          type="range"
          min={MIN}
          max={MAX}
          value={percent}
          onChange={(e) => handlePreview(Number((e.target as HTMLInputElement).value))}
          onPointerUp={(e) => handleCommit(Number((e.target as HTMLInputElement).value))}
          onMouseUp={(e) => handleCommit(Number((e.target as HTMLInputElement).value))}
          onTouchEnd={(e) => handleCommit(Number((e.target as HTMLInputElement).value))}
          className="trait-openclose-style03__slider"
          disabled={disabled}
        />
        <div className="trait-openclose-style03__markers" aria-hidden>
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>
  )
}
